import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import CardsSection from './CardsSection';
import Filters from './Filters';
import ImportExcel from './ImportExcel';
import ImportExcelAccordion from './ImportExcelAccordion';
import Header from './Header';
import CardConfigPanel from './CardConfigPanel.js';
import { buildApiUrl } from '../config/api';
import '../css/Dashboard.css';

const Dashboard = () => {
 
  
  // Stato per i dati delle card caricate dinamicamente
  const [dashboardData, setDashboardData] = useState([]);
  const [isLoadingCards, setIsLoadingCards] = useState(true);
  const [cardsStructure, setCardsStructure] = useState([]);
  const [hasLoadedCards, setHasLoadedCards] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [filteredData, setFilteredData] = useState({});
  const [userCode, setUserCode] = useState('');
  const [canImport, setCanImport] = useState(false);
  const [showCodeForm, setShowCodeForm] = useState(false);
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const isInitialMount = useRef(true);
  const importTimeoutRef = useRef(null); // Ref per gestire i timeout degli import
  const isImportingRef = useRef(false); // Ref per tracciare se siamo in fase di import
  const hasLoadedInitialData = useRef(false); // Ref per tracciare il primo caricamento dei dati
  
  const [filters, setFilters] = useState({
    Company: '', // Sarà popolato dinamicamente quando le companies vengono caricate
    View: 'Year',
    Year: '2025',
    Month: 'select..',
    'Compare With': 'Previous Year'  // Cambiato da 'select..' a 'Previous Year' per essere coerente
  });

  // Funzione per calcolare i parametri temporali basati sulla view e sul mese selezionato
  const calculateTimeParameters = (view, year, month) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // getMonth() restituisce 0-11
    const currentDay = currentDate.getDate();
    
    let fromMonth, fromDay, toMonth, toDay, fromYear, toYear;
    
    // Se è selezionato un mese specifico, filtra solo per quel mese indipendentemente dalla view
    if (month && month !== 'select..') {
      const monthMap = {
        'January': 1, 'February': 2, 'March': 3, 'April': 4,
        'May': 5, 'June': 6, 'July': 7, 'August': 8,
        'September': 9, 'October': 10, 'November': 11, 'December': 12
      };
      
      fromYear = parseInt(year);
      toYear = parseInt(year);
      fromMonth = monthMap[month];
      fromDay = 1;
      toMonth = monthMap[month];
      toDay = new Date(parseInt(year), monthMap[month], 0).getDate(); // Ultimo giorno del mese
    } else {
      // Ora abbiamo solo Year disponibile, usa sempre la logica Year to Date
      fromYear = currentYear;
      toYear = currentYear;
      fromMonth = 1;
      fromDay = 1;
      toMonth = currentMonth;
      toDay = currentDay;
    }
    
    return {
      fromYear,
      toYear,
      fromMonth,
      fromDay,
      toMonth,
      toDay
    };
  };

  // Funzione per recuperare i dati filtrati dall'API
  const fetchFilteredData = async (companyName, timeParams) => {
    try {
      // Trova l'ID della company selezionata
      const selectedCompany = companies.find(c => c.name === companyName);
      if (!selectedCompany) {
        console.error('Company non trovata:', companyName);
        return {}; // Ritorna oggetto vuoto se company non trovata
      }

      // Prepara i parametri per l'API
      const apiParams = {
        companyId: selectedCompany.id,
        fromYear: timeParams.fromYear,
        toYear: timeParams.toYear,
        fromMonth: timeParams.fromMonth,
        fromDay: timeParams.fromDay,
        toMonth: timeParams.toMonth,
        toDay: timeParams.toDay
      };

      // Gestione logica CompareWith basata sulla selezione del filtro
      const compareWithSelection = filters['Compare With'];
      console.log('Filtro Compare With selezionato:', compareWithSelection);
      
      if (compareWithSelection === 'Previous Year') {
        apiParams.compareWith = 1;  // Confronta con 1 anno prima
        console.log(`Confronto con anno precedente: compareWith = 1`);
      } else if (compareWithSelection === 'Year -2') {
        apiParams.compareWith = 2;  // Confronta con 2 anni prima
        console.log(`Confronto con anno -2: compareWith = 2`);
      } else {
        console.log('Nessun confronto temporale selezionato, parametro compareWith non inviato');
      }
      
      console.log('Parametri API completi:', apiParams);

      // Chiamata all'API filtered-row-values
      const response = await axios.get(buildApiUrl('import-excel/filtered-row-values'), {
        params: apiParams
      });

      if (response.status === 200) {
        const data = response.data.data;
        console.log('Dati filtrati ricevuti:', data);
        
        // Converti i dati in un formato più facile da utilizzare
        const dataMap = {};
        if (data && Array.isArray(data)) {
          data.forEach(item => {
            const key = `${item.dimRowId}_${item.dimCardId}`;
            dataMap[key] = {
              value: item.value,
              compareYearValue: item.compareYearValue || null
            };
          });
        }
        
        setFilteredData(dataMap);
        return dataMap;
      }
    } catch (error) {
      console.error('Errore durante il recupero dei dati filtrati:', error);
      return {}; // Ritorna oggetto vuoto in caso di errore
    }
    
    // Fallback: ritorna oggetto vuoto se non siamo arrivati al return precedente
    return {};
  };

  // Funzione per formattare i valori numerici (parentesi tonde per i negativi e punti per le migliaia)
  const formatNumberValue = (value) => {
    if (value === null || value === undefined || value === 0) {
      return '-';
    }
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return '-';
    }
    
    // Funzione per aggiungere i punti alle migliaia
    const addThousandSeparator = (num) => {
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };
    
    if (numValue < 0) {
      // Per i valori negativi, rimuovi il segno meno e metti tra parentesi tonde
      return `(${addThousandSeparator(Math.round(Math.abs(numValue)))})`;
    } else {
      // Per i valori positivi, mantieni il formato normale e aggiungi i punti
      return addThousandSeparator(Math.round(numValue));
    }
  };

  // Funzione per aggiornare i dati delle card con i valori reali
  const updateCardsWithRealData = (cards, dataMap) => {
    console.log('Aggiornamento card - Numero totale card:', cards.length);
    console.log('Aggiornamento card - Dati disponibili:', Object.keys(dataMap).length);
    
    // Verifica se è selezionato un confronto temporale
    const hasComparison = filters['Compare With'] === 'Previous Year' || filters['Compare With'] === 'Year -2';
    console.log('Confronto temporale attivo:', hasComparison ? filters['Compare With'] : 'Nessuno');
    
    const updatedCards = cards.map((card, cardIndex) => {
      console.log(`Aggiornamento card ${cardIndex + 1}: "${card.title}" - Righe: ${card.data.length}`);
      
      const scaleDivisor = card.divideBy1000 !== false ? 1000 : 1;
      // Costruisci tooltip periodo per la card
      const buildPeriodTooltip = () => {
        const { fromYear, toYear, fromMonth, fromDay, toMonth, toDay } = calculateTimeParameters(filters.View, filters.Year, filters.Month);
        const pad = (n) => n.toString().padStart(2, '0');
        const fmt = (y, m, d) => `${pad(d)}/${pad(m)}/${y}`;
        const p1From = fmt(fromYear, fromMonth, fromDay);
        const p1To = fmt(toYear, toMonth, toDay);

        let p2From = null, p2To = null;
        if (filters['Compare With'] === 'Previous Year' || filters['Compare With'] === 'Year -2') {
          const offset = filters['Compare With'] === 'Previous Year' ? 1 : 2;
          p2From = fmt(fromYear - offset, fromMonth, fromDay);
          p2To = fmt(toYear - offset, toMonth, toDay);
        }

        const periodPart = p2From && p2To
          ? `Periodo : da ${p1From}  a ${p1To} - da ${p2From}  a ${p2To}`
          : `periodo valore 1 (da ${p1From}  a ${p1To})`;

        const base = cards[cardIndex].infoDescription || '';
        return base ? `${base}. ${periodPart}` : periodPart;
      };

      const updatedCard = {
        ...card,
        tooltip: buildPeriodTooltip(),
        data: card.data.map((row, rowIndex) => {
          // Cerca i dati per questa riga usando dimRowId e dimCardId
          const key = `${row.dimRowId}_${row.dimCardId}`;
          const rowData = dataMap[key];
          
          if (rowData) {
            // Se abbiamo dati, usa i valori reali
            console.log(`  Riga ${rowIndex + 1}: "${row.label}" - Dati trovati: ${rowData.value}`);
            
            // Gestione valori di confronto (mostra 2025 anche se 2026 è 0)
            let value2, value3;
            const currentValueScaled = rowData.value / scaleDivisor;
            const hasCompareData =
              rowData.compareYearValue !== null && rowData.compareYearValue !== undefined;
            const compareValueScaled = hasCompareData
              ? rowData.compareYearValue / scaleDivisor
              : null;

            if (hasComparison && hasCompareData) {
              value2 = formatNumberValue(compareValueScaled);
              const diffRaw = currentValueScaled - compareValueScaled;
              const diffFormatted = formatNumberValue(diffRaw);
              const denominator = Math.abs(compareValueScaled);
              const percentFormatted =
                denominator !== 0
                  ? Math.round((diffRaw / denominator) * 100) + '%'
                  : diffRaw !== 0
                    ? '-'
                    : '0%';
              value3 = { diff: diffFormatted, pct: percentFormatted };
            } else {
              value2 = '-';
              value3 = '-';
            }
            
            return {
              ...row,
              value: formatNumberValue(currentValueScaled),
              value2: value2,
              value3: value3
            };
          } else {
            // Se NON abbiamo dati, imposta tutto a trattino
            console.log(`  Riga ${rowIndex + 1}: "${row.label}" - Nessun dato, imposto trattino`);
            return {
              ...row,
              value: '-',
              value2: '-',
              value3: '-'
            };
          }
        }),
        total: card.total.map(totalRow => {
          // Calcola il totale reale sommando i valori delle righe
          const totalValue = card.data.reduce((sum, row) => {
            if (row.isInTotal === false) { return sum; }
            const key = `${row.dimRowId}_${row.dimCardId}`;
            const rowData = dataMap[key];
            if (!rowData) { return sum; }
            const displayedInt = Math.round(rowData.value / scaleDivisor);
            return sum + displayedInt;
          }, 0);
          
          // Calcola il totale di confronto solo se è attivo il confronto temporale
          let totalCompareValue = 0;
          if (hasComparison) {
            totalCompareValue = card.data.reduce((sum, row) => {
              if (row.isInTotal === false) { return sum; }
              const key = `${row.dimRowId}_${row.dimCardId}`;
              const rowData = dataMap[key];
              if (!rowData || rowData.compareYearValue === null || rowData.compareYearValue === undefined) {
                return sum;
              }
              const displayedInt = Math.round(rowData.compareYearValue / scaleDivisor);
              return sum + displayedInt;
            }, 0);
          }
          
          // Calcola differenza e percentuale per il totale se è attivo il confronto
          const totalDiffRaw = totalValue - totalCompareValue;
          const totalDiffFormatted = hasComparison ? formatNumberValue(totalDiffRaw) : '-';
          const totalDenominator = Math.abs(totalCompareValue);
          const totalPercentFormatted = hasComparison && totalDenominator !== 0
            ? Math.round((totalDiffRaw / totalDenominator) * 100) + '%'
            : '-';
          const totalValue3 = hasComparison
            ? { diff: totalDiffFormatted, pct: totalPercentFormatted }
            : '-';
          
          console.log(`  Totale: ${totalRow.label} - Valore: ${totalValue}, Confronto: ${totalCompareValue}, Differenza: ${totalDiffRaw}, Percentuale: ${totalPercentFormatted}`);
          
          return {
            ...totalRow,
            value: formatNumberValue(totalValue),
            value2: hasComparison ? formatNumberValue(totalCompareValue) : '-',
            value3: totalValue3
          };
        })
      };
      
      return updatedCard;
    });
    
    console.log('Aggiornamento card completato - Card aggiornate:', updatedCards.length);
    return updatedCards;
  };

     // Funzione per caricare la struttura delle card e righe
   const loadCardsStructure = async () => {
     // FORZA sempre il caricamento dopo import (non evita chiamate duplicate)
     console.log('Caricamento struttura card e companies...');

    // Crea un AbortController per cancellare chiamate precedenti
    const abortController = new AbortController();

    try {
      // Chiamate concorrenti per companies e cards
      const [companiesResponse, cardsResponse] = await Promise.all([
        axios.get(buildApiUrl('import-excel/dim-companies'), {
          signal: abortController.signal
        }),
        axios.get(buildApiUrl('import-excel/dim-cards-with-rows'), {
          signal: abortController.signal
        })
      ]);
          
      if (companiesResponse.status === 200 && cardsResponse.status === 200) {
        const companiesData = companiesResponse.data.data;
        const cardsData = cardsResponse.data.data;
        
        // Imposta le companies
        setCompanies(companiesData);
        
        // Imposta la struttura delle card
        setCardsStructure(cardsData);
        
        // Trasforma i dati per creare le card
        const transformedCards = transformCardsStructureToDashboard(cardsData);
        setDashboardData(transformedCards);
        
        // Marca come caricate
        setHasLoadedCards(true);
        
        // Reset del flag di import quando le companies sono state caricate con successo
        if (isImportingRef.current) {
          console.log('Companies caricate con successo, reset flag di import');
          isImportingRef.current = false;
        }
        
        console.log('Companies caricate:', companiesData);
        console.log('Struttura card caricata:', cardsData);
        console.log('Card trasformate:', transformedCards);
      }
    } catch (error) {
      // Ignora errori di cancellazione
      if (error.name === 'AbortError') {
        console.log('Chiamata API cancellata');
        return;
      }
      
      console.error('Errore durante il caricamento della struttura card e companies:', error);
      // In caso di errore, mostra card di fallback
      setDashboardData(createFallbackCards());
      setHasLoadedCards(true); // Marca come caricate anche in caso di errore
      
      // Reset del flag di import in caso di errore
      if (isImportingRef.current) {
        console.log('Errore durante caricamento, reset flag di import');
        isImportingRef.current = false;
      }
    } finally {
      setIsLoadingCards(false);
    }
 
     // Cleanup function per cancellare chiamate in corso
     return () => {
       abortController.abort();
     };
     
     // Ritorna una Promise per permettere l'uso di .then()
     return Promise.resolve();
   };

  // Funzione per trasformare la struttura delle card in formato dashboard
  const transformCardsStructureToDashboard = (cardsStructure) => {
    if (!cardsStructure || cardsStructure.length === 0) {
      return createFallbackCards();
    }

    return cardsStructure.map(card => ({
      title: card.name,
      textColor: 'text-white',
      dimCardId: card.id,
      divideBy1000: (card.divideBy1000 ?? card.DivideBy1000) !== false,
      isQuantity: card.isQuantity === true || card.IsQuantity === true,
      infoDescription: card.infoDescription || '',
      data: card.rows.map(row => ({
        label: row.description,
        dimRowId: row.id, // Aggiungo l'ID della riga per il mapping
        dimCardId: card.id, // Aggiungo l'ID della card per il mapping
        isInTotal: row.isInTotal,
        refUrl: row.refUrl || null, // Aggiungo l'URL di riferimento
        value: '0', // Per ora placeholder, poi si collegherà ai dati reali
        value2: '',
        value3: ''
      })),
      total: [{ 
        label: card.totalRowLabel || 'Totale', 
        value: card.rows.length > 0 ? `${card.rows.length} righe` : 'Nessuna riga' 
      }]
    }));
  };

  // Funzione per creare card di fallback in caso di errore
  const createFallbackCards = () => {
    return [
      {
        title: 'Conto Economico',
        textColor: 'text-white',
        data: [
          { label: 'Ricavi', value: '60', value2: '', value3: '' },
          { label: 'Margine Lordo', value: '(54)', value2: '', value3: '' },
          { label: 'Costo de lavoro', value: '(192)', value2: '', value3: '' },
          { label: 'Spese generali', value: '(565)', value2: '', value3: '' },
        ],
        total: [{ label: 'EBITDA', value: '(812)', value2: '', value3: '' }]
      },
      {
        title: 'Stato Patrimoniale Attivo',
        textColor: 'text-white',
        data: [
          { label: 'Attività immateriali', value: '676', value2: '', value3: '' },
          { label: 'Attività materiali', value: '15.770', value2: '', value3: '' },
          { label: 'Fondo ammortamento', value: '(1.524)', value2: '', value3: '' },
          { label: 'Attività correnti', value: '1.670', value2: '', value3: '' }
        ],
        total: [{ label: 'Totale attivo', value: '16.592', value2: '', value3: '' }]
      }
    ];
  };

  // Carica la struttura delle card al mount del componente
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Carica la struttura delle card senza mostrare il modale
      loadCardsStructure();
    }
    
    // Cleanup function per cancellare i timeout quando il componente viene smontato
    return () => {
      if (importTimeoutRef.current) {
        clearTimeout(importTimeoutRef.current);
        console.log('Timeout import cancellato al cleanup');
      }
    };
  }, []);

  const handleCodeSubmit = (e) => {
    e?.preventDefault?.();
    const codeInput = (e?.target?.elements?.code?.value || '').trim();
    if (!codeInput) {
      alert('Inserisci un codice valido');
      return;
    }
    localStorage.setItem('cockpit_user_code', codeInput);
    setUserCode(codeInput);
    setShowCodeForm(false);
  };

  const showCodeModal = () => {
    setShowCodeForm(true);
  };

  const closeCodeModal = () => {
    setShowCodeForm(false);
  };

  // Controlla se c'è già un codice salvato nel localStorage
  useEffect(() => {
    const savedCode = localStorage.getItem('cockpit_user_code');
    if (savedCode) {
      setUserCode(savedCode);
    }
    // Non imposta nessun codice predefinito al primo accesso
  }, []);

  // Gestisce la chiusura del modale con il tasto ESC
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showCodeForm) {
        closeCodeModal();
      }
    };

    if (showCodeForm) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showCodeForm]);

  

  // Effetto per verificare l'autorizzazione quando cambia il codice
  useEffect(() => {
    if (userCode) {
      console.log('Codice cambiato, verifico autorizzazione per:', userCode);
      console.log('URL API:', buildApiUrl('import-excel/can-import'));
      console.log('Parametri:', { code: userCode });
      
      // Per ora, gestiamo l'autorizzazione solo dal frontend
      // Il codice "Ab1cD2eF3gH" è sempre autorizzato
      if (userCode === 'Ab1cD2eF3gH') {
        setCanImport(true);
        console.log('Autorizzazione verificata: true (codice valido)');
      } else {
        setCanImport(false);
        console.log('Autorizzazione negata: codice non valido');
      }
    } else {
      console.log('Nessun codice utente, imposto canImport a false');
      setCanImport(false);
    }
  }, [userCode]);



  // Effetto per recuperare i dati filtrati quando cambiano i filtri
  useEffect(() => {
    console.log('useEffect filtri attivato con:', {
      companiesLength: companies.length,
      cardsStructureLength: cardsStructure.length,
      filtersCompany: filters.Company,
      hasLoadedCards,
      isImportingRef: isImportingRef.current,
      hasLoadedInitialData: hasLoadedInitialData.current
    });
    
    // Solo se abbiamo companies, cards e una company selezionata
    // E NON siamo nel processo di import/eliminazione (evita chiamate duplicate)
    if (companies.length > 0 && cardsStructure.length > 0 && filters.Company && filters.Company !== '' && hasLoadedCards && !isImportingRef.current) {
      
      // Se è il primo caricamento, marca come completato e procedi
      if (!hasLoadedInitialData.current) {
        console.log('Primo caricamento, recupero dati iniziali...');
        hasLoadedInitialData.current = true;
      } else {
        console.log('Filtri cambiati, recupero dati filtrati...');
      }
      
      // Calcola i parametri temporali basati sulla view
      const timeParams = calculateTimeParameters(filters.View, filters.Year, filters.Month);
      console.log('Parametri temporali calcolati:', timeParams);
      
      // Recupera i dati filtrati
      fetchFilteredData(filters.Company, timeParams).then(dataMap => {
        // IMPORTANTE: Aggiorna SEMPRE tutte le card, anche se dataMap è vuoto
        // Questo assicura che le card vengano "reset" e mostrino 0 per le celle senza dati
        const updatedCards = updateCardsWithRealData(dashboardData, dataMap || {});
        setDashboardData(updatedCards);
        console.log('Card aggiornate con dati reali (incluse celle con 0):', updatedCards);
      }).catch((error) => {
        console.error('Errore durante il recupero dei dati filtrati:', error);
        // In caso di errore, resetta il flag di import per permettere future chiamate
        if (isImportingRef.current) {
          console.log('Reset flag di import dopo errore nei filtri');
          isImportingRef.current = false;
        }
      });
    } else {
      console.log('Condizioni non soddisfatte per recuperare dati filtrati:', {
        companiesLength: companies.length,
        cardsStructureLength: cardsStructure.length,
        filtersCompany: filters.Company,
        hasLoadedCards,
        isImportingRef: isImportingRef.current,
        hasLoadedInitialData: hasLoadedInitialData.current
      });
    }
  }, [filters, companies, cardsStructure, hasLoadedCards]); // Dipendenze: filtri, companies, struttura card e stato caricamento

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    console.log('Filters received by parent:', newFilters);
  };

  // Ref per chiamare la funzione di refresh dei filtri
  const refreshFiltersRef = useRef(null);

  // Funzione per notificare che i filtri devono essere aggiornati
  const refreshFiltersData = () => {
    // Questa funzione può essere chiamata dai componenti figli per forzare il refresh
    // dei dati dei filtri (anni e mesi disponibili)
    console.log('Refresh dei dati dei filtri richiesto');
    if (refreshFiltersRef.current) {
      refreshFiltersRef.current();
    }
  };

                   const handleImport = (importData) => {
      console.log('Dati importati:', importData);
      
      // Imposta il flag di import
      isImportingRef.current = true;
      
      // Cancella il timeout precedente se esiste
      if (importTimeoutRef.current) {
        clearTimeout(importTimeoutRef.current);
        console.log('Timeout precedente cancellato');
      }
      
      // Gestisce sia import Excel che eliminazione last-import
      const isDeleteAction = importData.action === 'delete-last-import';
      const actionType = isDeleteAction ? 'ELIMINAZIONE LAST-IMPORT' : 'IMPORT EXCEL';
      
      // FORZA il ricaricamento di TUTTE e TRE le API dopo ogni import/eliminazione
      console.log(`=== FORZATURA RICARICAMENTO COMPLETO DOPO ${actionType} ===`);
      console.log('Chiamo SEMPRE tutte e tre le API: dim-companies, dim-cards-with-rows, filtered-row-values');
      
      // Reset completo per forzare il ricaricamento
      setHasLoadedCards(false);
      setCompanies([]);
      setDashboardData([]);
      setFilteredData({});
      hasLoadedInitialData.current = false;
      
      // Notifica che i filtri devono essere aggiornati
      refreshFiltersData();
      
      // 1. SEMPRE chiama dim-companies e dim-cards-with-rows
      loadCardsStructure().then(() => {
        console.log('1. ✅ API dim-companies e dim-cards-with-rows completate');
        
        // 2. SEMPRE chiama filtered-row-values se c'è una company selezionata
        if (filters.Company && filters.Company !== '') {
          console.log('2. ✅ Chiamata API filtered-row-values...');
          const timeParams = calculateTimeParameters(filters.View, filters.Year, filters.Month);
          fetchFilteredData(filters.Company, timeParams).then(dataMap => {
            console.log('3. ✅ API filtered-row-values completata, aggiorno le card...');
            if (dashboardData.length > 0) {
              const updatedCards = updateCardsWithRealData(dashboardData, dataMap || {});
              setDashboardData(updatedCards);
              console.log(`Card aggiornate con nuovi dati dopo ${actionType.toLowerCase()}:`, updatedCards.length);
            }
            // Reset del flag di import dopo il completamento
            isImportingRef.current = false;
            console.log(`=== TUTTE E TRE LE API COMPLETATE CON SUCCESSO DOPO ${actionType} ===`);
          }).catch((error) => {
            console.error('Errore API filtered-row-values:', error);
            // Anche in caso di errore, resetta il flag
            isImportingRef.current = false;
            console.log(`Flag di import resettato dopo errore filtered-row-values (${actionType})`);
          });
        } else {
          console.log('2. ⚠️ Nessuna company selezionata, skip API filtered-row-values');
          // Reset del flag comunque
          isImportingRef.current = false;
          console.log(`=== DUE API COMPLETATE (terza saltata per mancanza company) DOPO ${actionType} ===`);
        }
      }).catch((error) => {
        // Gestione errori: anche in caso di errore, resetta il flag
        console.error('Errore durante il caricamento struttura:', error);
        isImportingRef.current = false;
        console.log(`Flag di import resettato dopo errore struttura (${actionType})`);
      });
      
      // Timeout di sicurezza per resettare il flag di import se qualcosa va storto
      setTimeout(() => {
        if (isImportingRef.current) {
          console.log('Timeout di sicurezza: reset flag di import');
          isImportingRef.current = false;
        }
      }, 10000); // 10 secondi di timeout
    };

  // Mostra loading mentre si caricano le card
  if (isLoadingCards) {
    return (
      <div>
        <Header title="Cockpit" onShowCodeModal={showCodeModal} />
        <div className="container">
          <div className="loading-cards">
            <div className="spinner"></div>
            <p>Caricamento struttura card e companies...</p>
          </div>
        </div>
      </div>
    );
  }

  const handleConfigChanged = () => {
    // Ricarica le card quando la configurazione cambia
    loadCardsStructure();
  };

  return (
    <div>
      <Header 
        title="Cockpit" 
        onShowCodeModal={showCodeModal}
        onShowConfigPanel={canImport ? () => setShowConfigPanel(true) : undefined}
      />
      <CardConfigPanel 
        isOpen={showConfigPanel}
        onClose={() => setShowConfigPanel(false)}
        onConfigChanged={handleConfigChanged}
      />
      
             <div className="container">
                                       {/* Modale per inserimento codice */}
          {showCodeForm && (
            <div 
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
              }}
              onClick={closeCodeModal} // Chiude cliccando fuori
            >
              <div 
                style={{
                  backgroundColor: 'white',
                  padding: '30px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                  minWidth: '400px',
                  textAlign: 'center',
                  position: 'relative' // Per posizionare il pulsante di chiusura
                }}
                onClick={(e) => e.stopPropagation()} // Previene la chiusura cliccando sul contenuto
              >
                {/* Pulsante di chiusura */}
                <button
                  onClick={closeCodeModal}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '15px',
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f0f0f0';
                    e.target.style.color = '#333';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#666';
                  }}
                  title="Chiudi"
                >
                  ×
                </button>
                               <h3 style={{ margin: '0 0 20px 0', color: '#333' }}>
                  {userCode && userCode !== 'Ab1cD2eF3gH' ? 'Accesso Import Excel' : 'Accesso Import Excel'}
                </h3>
                <form onSubmit={handleCodeSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div>
                    <label htmlFor="code" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#555' }}>
                      {userCode && userCode !== 'Ab1cD2eF3gH' ? 'Codice errato' : 'Codice di autorizzazione:'}
                    </label>
                    <input 
                       id="code" 
                       name="code" 
                       type="text" 
                       placeholder="Inserisci il codice" 
                       defaultValue={userCode || ''}
                       required
                      style={{ 
                        width: '100%', 
                        padding: '12px', 
                        border: '1px solid #ddd', 
                        borderRadius: '4px', 
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }} 
                    />
                  </div>
                                   <button 
                    type="submit" 
                    style={{ 
                      padding: '12px 24px', 
                      backgroundColor: '#20362C', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      fontSize: '14px', 
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                                         {userCode && userCode !== 'Ab1cD2eF3gH' ? 'Aggiorna Codice' : 'Verifica Autorizzazione'}
                  </button>
               </form>
             </div>
           </div>
         )}
         
         
        {canImport && (
          <ImportExcelAccordion onImport={handleImport} />
        )}
        <Filters onFiltersChange={handleFiltersChange} companies={companies} onRefreshFilters={refreshFiltersRef}/>
        
        {dashboardData.length > 0 ? (
          <CardsSection 
            cardsData={dashboardData}
            filters={filters} 
          />
        ) : (
          <div className="no-cards-message">
            <p>Nessuna card disponibile. Verifica la connessione al database.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;